import type { MouseEvent } from 'react';
import type { Product } from '@/types/product';
import { navigate } from '@/hooks/useRoute';
import { useStore } from '@/context/StoreContext';
import './ProductCard.css';

const money=(n:number)=>new Intl.NumberFormat('he-IL',{
 style:'currency',
 currency:'ILS',
 maximumFractionDigits:0
}).format(n);

const membership=(p:Product,key:string)=>
 p.category===key||(p.categoryKeys||[]).includes(key);

export function ProductCard({product}:{product:Product}){
 const {wishlist,toggleWishlist,toggleCompare,addToCart}=useStore();
 const wished=wishlist.includes(product.id);

 const hiddenImages=new Set(product.hiddenImages||[]);
 const visibleGallery=(product.gallery||[]).filter(img=>img&&!hiddenImages.has(img));
 const primaryImage=(!hiddenImages.has(product.image)&&product.image)||visibleGallery[0]||product.image;
 const secondaryImage=(!hiddenImages.has(product.hoverImage)&&product.hoverImage)||visibleGallery.find(img=>img!==primaryImage)||'';
 const hasSecondary=Boolean(secondaryImage && secondaryImage!==primaryImage);
 const productHref=`/product/${product.slug}`;

 const cleanJewelryLabel=(value:string)=>String(value||'')
  .replace(/טבעות אירוסין/g,'טבעות מוסונייט')
  .replace(/טבעת אירוסין/g,'טבעת מוסונייט')
  .replace(/טבעות נישואין/g,'טבעות מוסונייט')
  .replace(/יהלומי מעבדה|יהלום מעבדה|יהלומים|יהלום/g,'מוסונייט')
  .replace(/\s*-\s*/g,' — ')
  .trim();

 const displayName=cleanJewelryLabel(product.name);

 const openProduct=(e:MouseEvent<HTMLAnchorElement>)=>{
  e.preventDefault();
  navigate(productHref);
 };

 const metaParts=[
  'מוסונייט',
  product.carat||'',
  product.color||'',
  product.clarity||'',
  'כסף 925'
 ].filter(Boolean);

 const isNew=membership(product,'new');

 const badge=
  product.oldPrice>product.price
   ? {label:'SALE',tone:'sale'}
   : isNew
    ? {label:'NEW',tone:'new'}
    : product.featured
     ? {label:'מומלץ',tone:'featured'}
     : product.readyToShip
      ? {label:'מוכן למשלוח',tone:'ready'}
      : null;

 const needsOptions=
  ['engagement','rings','wedding'].some(key=>membership(product,key)) ||
  (product.metals?.length||0)>1 ||
  (product.purities?.length||0)>1 ||
  product.engraving;

 const primaryAction=()=>{
  if(needsOptions)navigate(productHref);
  else addToCart(product.id);
 };

 return (
  <article className={`product-card luvera-card-v118${hasSecondary?' has-secondary':''}`}>
   <div className="luvera-card-shell">
    <div className="product-media">
     <a
      className="product-image-link"
      href={productHref}
      onClick={openProduct}
      aria-label={`לצפייה ב${displayName}`}
     >
      <img
       className="product-image product-image-primary"
       src={primaryImage}
       alt={displayName}
       loading="lazy"
      />
      {hasSecondary&&
       <img
        className="product-image product-image-secondary"
        src={secondaryImage}
        alt=""
        aria-hidden="true"
        loading="lazy"
       />
      }
     </a>

     {badge&&
      <span className={`luvera-card-badge ${badge.tone}`}>
       {badge.label}
      </span>
     }

     <div className="card-actions">
      <button
       className={`wish-action${wished?' active':''}`}
       onClick={()=>toggleWishlist(product.id)}
       aria-label={wished?'הסרה מהמועדפים':'הוספה למועדפים'}
      >
       {wished?'♥':'♡'}
      </button>

      <button
       className="compare-action"
       onClick={()=>toggleCompare(product.id)}
       aria-label="השוואה"
      >
       ⇄
      </button>
     </div>
        <button
      className={`quick-add quick-add-media${needsOptions?' choose-options':''}`}
      onClick={primaryAction}
     >
      {needsOptions?'לפרטים ובחירת אפשרויות':'הוספה לסל'}
      <span aria-hidden="true">←</span>
     </button>
   </div>

    <div className="product-copy product-card-below-copy">
     <div className="luvera-collection-line" aria-hidden="true">
      <span></span>
      <small>MIELLEY COLLECTION</small>
      <span></span>
     </div>

     <h3>
      <a href={productHref} onClick={openProduct}>{displayName}</a>
     </h3>

     <div className="luvera-title-separator" aria-hidden="true">
      <span></span><i>✦</i><span></span>
     </div>

     <p className="product-meta" aria-label="מפרט מקוצר">
      {metaParts.map((part,i)=>
       <span key={`${part}-${i}`} className="product-meta-part">
        {i>0&&<span className="meta-separator" aria-hidden="true"> · </span>}
        {part}
       </span>
      )}
     </p>

     <div className="price-line">
      <strong>{money(product.price)}</strong>
      {product.oldPrice>product.price&&<del>{money(product.oldPrice)}</del>}
     </div>

     <div className="mielley-mobile-cta-only"><button
      className={`quick-add quick-add-mobile${needsOptions?' choose-options':''}`}
      onClick={primaryAction}
     >
      {needsOptions?'לפרטים ובחירת אפשרויות':'הוספה לסל'}
      <span aria-hidden="true">←</span>
     </button></div>
    </div>
   </div>
  </article>
 );
}
